import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useQuery } from '@apollo/client';
import {
  CustomModal,
  EventEmitter,
  getTranslatedText,
  Loader,
  NavHeader
} from '@nile/components';
import {
  Button,
  ButtonGroup,
  Heading,
  Tag,
  TextField
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { debounce } from 'lodash';
import { AccountClient, GET_SERVICE_CATEGORIES } from '@nile/graphql';
import { ServiceItemListTable } from '@nile/client_management/src/Containers/ClientDetails/ClientContractNew/ContractForm/Components';
import { ServiceCatalogItem } from '@nile/types';
import { CreateServiceCatalogItemButton } from '@nile/banyan/src/Pages/ServiceCatalogNew/Components';

export const ServiceItemsPopup = ({
  title,
  selectedItems,
  setSelectedItems,
  filterItems,
  closeModal,
  serviceItemFetchConditions
}: {
  title?: string;
  selectedItems?: ServiceCatalogItem[];
  setSelectedItems?: (items) => void;
  filterItems?: string[];
  closeModal: () => void;
  serviceItemFetchConditions?: {
    joinOperator: string;
    operands: any[];
  };
}) => {
  const intl = useIntl();

  const [filterText, setFilterText] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchFilteredData = (text) => {
    EventEmitter.dispatch('filterData', text);
  };

  const debouncedFilter = useCallback(debounce(fetchFilteredData, 1000), []);

  const { loading, data, error } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient
  });

  const ServiceItemListTableRef = useRef();

  const [records, setRecords] = useState<ServiceCatalogItem[]>(
    selectedItems || []
  );

  const allCategories =
    !loading && data.getServiceCategories
      ? data.getServiceCategories.slice()
      : [];

  function updateSetRecords(value) {
    setRecords(value);
  }

  const serviceItemCategoryFetchConditions = useMemo(() => {
    if (selectedCategory === 'all') {
      return serviceItemFetchConditions;
    }

    if (serviceItemFetchConditions) {
      return {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'category.name',
            operator: 'is',
            value: selectedCategory
          },
          ...serviceItemFetchConditions?.operands
        ]
      };
    }
  }, [serviceItemFetchConditions, selectedCategory]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="w-80vw vh80 bg-white br10">
      <div className="fx actr fw p20 divider">
        <Heading element="h1" weight="Bold">
          <FormattedMessage id={title || 'admin.serviceCatalog.header'} />
        </Heading>
        <div className="mlauto mr-20">
          {records?.length ? (
            <Tag secondary selected>
              {`${records?.length?.toString()} ${getTranslatedText(
                intl,
                'app.common.selected'
              )}`}
            </Tag>
          ) : null}
        </div>
        <div className="mr-20">
          <TextField
            labelHidden
            value={filterText}
            label="ServiceCatalogSearch"
            placeholder={getTranslatedText(
              intl,
              'invoiceBuilder.catalogItems.search.placeholder'
            )}
            onChange={(val) => {
              setFilterText(val);
              // setting this to null as it's just a gimmick to focus all categories. Else API call will go for all instead of filtered text.
              setSelectedCategory('');
              debouncedFilter(val);
            }}
            autoComplete="off"
            prefix={<SVGIcon name="Search" />}
            suffix={
              filterText ? (
                <div
                  className="position-relative z21"
                  onClick={() => {
                    setFilterText('');
                    debouncedFilter('');
                  }}
                >
                  <SVGIcon name="CircleCancelMinor" />
                </div>
              ) : null
            }
            size="small"
            variant="outline"
          />
        </div>
        <ButtonGroup>
          <Button onClick={closeModal} plainSecondary>
            <FormattedMessage id="common.cancel" />
          </Button>
          <CreateServiceCatalogItemButton
            onSuccess={(item) => {
              updateSetRecords([...records, item]);
              setSelectedCategory('all');
              setFilterText('');
            }}
          />
          <Button
            primary
            disabled={!records?.length}
            icon={<SVGIcon name="Save" />}
            onClick={() => {
              if (setSelectedItems) {
                setSelectedItems(records);
              }
              closeModal();
            }}
          >
            <FormattedMessage id="common.add" />
          </Button>
        </ButtonGroup>
      </div>
      <div className="fx actr ch-90">
        <div className="fhp scroll w300 p10 border-right border-top">
          <div className="fx py-2 pt10 pb10 mt10 mb8 border-bottom f-12 bold">
            <NavHeader>
              <FormattedMessage id="invoiceBuilder.itemSelection.modal.serviceCategories" />
            </NavHeader>
          </div>
          <div className="LinkGroup mb40">
            <div
              className={`fx actr ticketing-actions p10 br-5 ptr ${
                selectedCategory === 'all' || !selectedCategory
                  ? 'lightpurple-to-black-bg font-weight-600'
                  : 'bg-none'
              }`}
              key="category-all"
              onClick={() => {
                setSelectedCategory('all');
                setFilterText('');
              }}
            >
              <FormattedMessage id="invoiceBuilder.itemSelection.allCategories" />
            </div>
            {allCategories.map((el) => {
              return (
                <div
                  className={`fx actr ticketing-actions p10 br-5 ptr ${
                    el.name === selectedCategory
                      ? 'lightpurple-to-black-bg font-weight-600'
                      : 'bg-none'
                  }`}
                  key={`category-${el.name}`}
                  onClick={() => {
                    setSelectedCategory(el.name);
                    setFilterText('');
                  }}
                >
                  {el.label || el.name}
                </div>
              );
            })}
          </div>
        </div>

        <div className="fhp cw-300">
          <ServiceItemListTable
            mode="VIEW"
            actions={{
              editServiceItem: () => {},
              cloneServiceItem: () => {}
            }}
            filterItems={filterItems}
            actionsRef={ServiceItemListTableRef}
            records={records}
            setRecords={updateSetRecords}
            defaultConditions={serviceItemCategoryFetchConditions}
          />
        </div>
      </div>
    </div>
  );
};
